import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { useTheme } from "@mui/material/styles";

import {
  Button,
  CloseButton,
  FormTextField,
  LoadingOverlay,
  SubmitButton,
} from "@covertree/ui";
import FormFileField from "@covertree/ui/forms/FormFileField";
import FormCoverageDocument from "@covertree/ui/forms/FormCoverageDocument";

import { useFormContext } from "react-hook-form";

const FOUR_AND_HALF_MB = 4.5 * 1024 * 1024;

interface UploadProofOfCoverageModalProps {
  onClose: () => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

function UploadProofOfCoverageModalContent({
  onClose,
  isLoading,
  isSuccess,
  isError,
}: UploadProofOfCoverageModalProps) {
  const theme = useTheme();

  const {
    watch,
    setValue,
    control,
    resetField,
    reset,
    formState: { isValid },
  } = useFormContext();

  const files = watch("files") || [];

  const removeDocument = (document: File) => {
    setValue(
      "files",
      files.filter((file) => document.name !== file.name)
    );
    if (files.length === 1) {
      resetField("description");
    }
  };
  const handleClose = () => {
    onClose();
    reset();
  };

  if (isLoading)
    return (
      <Stack p={3} height={300}>
        <LoadingOverlay />
      </Stack>
    );

  if (isSuccess) {
    return (
      <Stack spacing={4} alignItems="flex-start" justifyContent="space-around">
        <Stack
          width="100%"
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start">
          <img
            src="/images/icons/checkmark.webp"
            alt="success"
            width={80}
            height={80}
          />
          <CloseButton onClick={handleClose} />
        </Stack>
        <Stack>
          <Typography fontSize={theme.spacing(4)}>
            Proof Of Coverage Uploaded
          </Typography>
        </Stack>
        <Stack>
          <Typography fontSize={theme.spacing(2.5)}>
            Thank you for uploading the proof of coverage. Someone from our team
            will get back to you soon.
          </Typography>
        </Stack>
        <Stack>
          <Typography fontSize={theme.spacing(2.5)}>
            In case of any questions, please call a CoverTree agent at{" "}
            <Typography component="span" color="secondary.main">
              (877) 417-8733
            </Typography>{" "}
            or email us at{" "}
            <Typography component="span" color="secondary.main">
              help@covertree.com
            </Typography>
            .
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: { xs: 7, md: 0 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">Upload Proof Of Coverage</Typography>
        <CloseButton onClick={handleClose} />
      </Stack>
      {isError && (
        <FormHelperText error>
          Something went wrong.
          <br /> The following documents were not uploaded correctly. Please try
          again.
        </FormHelperText>
      )}
      <FormFileField
        name="files"
        maxFileSize={FOUR_AND_HALF_MB}
        maxFileAmount={10}
        accept={{
          "image/png": [".png"],
          "image/jpeg": [".jpg"],
          "application/pdf": [".pdf"],
        }}
      />
      {files.map((file: File) => (
        <FormCoverageDocument
          key={file.name}
          document={file}
          removeDocument={removeDocument}
        />
      ))}
      <FormTextField
        id="description"
        name="description"
        label="Description"
        control={control}
      />
      <Stack direction="row" spacing={2}>
        <SubmitButton
          width={250}
          disabled={!files.length || !isValid || isLoading}>
          Upload Document
        </SubmitButton>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}

export default UploadProofOfCoverageModalContent;
